import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useUpdateEffect } from "react-use";
import { Avatar, AvatarGroup, PersonIcon, Select } from "@activecollab/components";
import { difference, intersection, isEqual } from "lodash";
import PropTypes from "prop-types";
import { useGenericAvatarUrl } from "~/components/Hooks/useGenericAvatarUrl";
import { getCalendarMembersByCalendarId } from "~/store/selectors/selector.calendars";
import { getActiveUsers } from "~/store/selectors/selector.users";
import { replaceAvatarSize } from "~/utils/utils";
import { SubscribersAvatarList } from "../SubscribersAvatarList/SubscribersAvatarList";

export const CalendarEventParticipants = ({ calendarId, participants, canEdit, onClose, allowAnonymous = false }) => {
  const { t } = useTranslation();
  const calendarMembersIds = useSelector((state) => getCalendarMembersByCalendarId(state, calendarId));
  const activeUsers = useSelector(getActiveUsers);

  const { getAvatarUrl } = useGenericAvatarUrl();

  const calendarMembers = useMemo(() => {
    return calendarMembersIds.reduce((prev, u) => {
      const user = activeUsers.get(`${u}`);
      if (user) {
        prev.push(user.toJS());
      }
      return prev;
    }, []);
  }, [activeUsers, calendarMembersIds]);

  const [selected, setSelected] = useState(() => participants);

  const options = useMemo(() => {
    if (!allowAnonymous) {
      return calendarMembers.map((user) => ({
        id: user.email,
        name: user.display_name,
        image: user.avatar_url.replace("--SIZE--", "24"),
        class: user.class,
      }));
    }
    return [];
  }, [allowAnonymous, calendarMembers]);

  const selectedUsers = useMemo(() => {
    return calendarMembers.reduce((prev, next) => {
      if (participants.includes(next.email)) {
        prev.push(next.id);
      }
      return prev;
    }, []);
  }, [calendarMembers, participants]);

  const handleChange = useCallback((value) => {
    setSelected(value);
  }, []);

  const handleOpen = useCallback(() => {
    if (!isEqual(participants, selected)) {
      setSelected(participants);
    }
  }, [participants, selected]);

  const handleClose = useCallback(() => {
    if (typeof onClose === "function") {
      onClose(selected);
    }
  }, [onClose, selected]);

  useUpdateEffect(() => {
    setSelected(participants);
  }, [participants]);

  useEffect(() => {
    if (canEdit) {
      const calendarMembersEmails = calendarMembers.map((m) => m.email);
      if (calendarId && difference(selected, calendarMembersEmails).length > 0) {
        const users = intersection(selected, calendarMembersEmails);
        onClose(users);
      }
    }
  }, [calendarMembers, onClose, selected, calendarId, canEdit]);

  if (allowAnonymous) {
    return selected.length > 0 ? (
      <AvatarGroup
        limit={5}
        size={24}
        className="tw-cursor-default hover:tw-bg-transparent"
        hasTooltip
      >
        {selected.map((email) => {
          const user = calendarMembers.find((u) => u.email === email);
          return user ? (
            <Avatar
              key={user.id}
              alt={user.display_name}
              url={replaceAvatarSize(user.avatar_url, 24)}
            />
          ) : (
            <Avatar
              key={email}
              alt={email}
              url={getAvatarUrl(email)}
            />
          );
        })}
      </AvatarGroup>
    ) : (
      <PersonIcon />
    );
  }

  if (!canEdit) {
    return selected.length > 0 ? (
      <AvatarGroup
        limit={5}
        size={24}
        className="tw-cursor-default hover:tw-bg-transparent"
        hasTooltip
      >
        {selectedUsers.map((userId) => {
          const user = calendarMembers.find((u) => u.id === userId);
          return (
            <Avatar
              key={user.id}
              alt={user.display_name}
              url={replaceAvatarSize(user.avatar_url, 24)}
            />
          );
        })}
      </AvatarGroup>
    ) : (
      <PersonIcon />
    );
  }

  return (
    <Select
      target={
        <SubscribersAvatarList
          subscriberIds={selectedUsers}
          addTitle={t("Add Participants")}
          editTitle={t("Edit Participants")}
        />
      }
      selected={selected}
      options={options}
      onChange={handleChange}
      onSelectClose={handleClose}
      onSelectOpen={handleOpen}
      type="multiple"
    />
  );
};

CalendarEventParticipants.propTypes = {
  calendarId: PropTypes.number,
  participants: PropTypes.array,
  canEdit: PropTypes.bool,
  allowAnonymous: PropTypes.bool,
  onClose: PropTypes.func,
};

CalendarEventParticipants.defaultProps = {
  canEdit: true,
};
